import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { auth, db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../styles/SubmitTeamSummaryForm.css';
import { FiChevronLeft } from 'react-icons/fi';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TopBar from '../components/TopBar';
import EnemyStatsForm from './EnemyStatsForm';
import MirrorSubmissionModals from './MirrorSubmissionModals';
import { getCurrentSeason, getMapPoolForSeason } from '../data/seasons';
import { useMirrorSubmission } from '../hooks/useMirrorSubmission';
import {
  findMatchTypeEntry,
  getMatchTypeOptions,
  getMatchTypeRegions,
  getSeasonConfig,
} from '../utils/matchTypeCatalog';

const WRITE_SEASON_ID = getCurrentSeason();

const AddEnemyDashboard = () => {
  const [searchParams] = useSearchParams();
  const teamIdFromQuery = searchParams.get('teamId');
  const modeIdFromQuery = searchParams.get('modeId');
  const seasonIdFromQuery = searchParams.get('seasonId');

  const [formRef] = useState(useRef(null));
  const [userProfile, setUserProfile] = useState(null);
  const [teamId, setTeamId] = useState(null);
  const [seasonId, setSeasonId] = useState(
    modeIdFromQuery ? (seasonIdFromQuery || WRITE_SEASON_ID) : WRITE_SEASON_ID
  );
  const [teamLogo, setTeamLogo] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [matchDate, setMatchDate] = useState('');
  const [region, setRegion] = useState('');
  const [enemyTeam, setEnemyTeam] = useState('');
  const [mode, setMode] = useState('');
  const [selectedMap, setSelectedMap] = useState('');
  const [matchType, setMatchType] = useState('');
  const [matchCategory, setMatchCategory] = useState('PRACTICE');
  const [matchTypeRegion, setMatchTypeRegion] = useState('GLOBAL');
  const [pointsFor, setPointsFor] = useState('');
  const [pointsAgainst, setPointsAgainst] = useState('');
  const [matchResult, setMatchResult] = useState('');
  const [mapOptions, setMapOptions] = useState([]);
  const [matchTypes, setMatchTypes] = useState([]);
  const [matchTypeCatalog, setMatchTypeCatalog] = useState([]);
  const [vsTeams, setVsTeams] = useState([]);
  const [regions, setRegions] = useState([]);
  const [teamRegion, setTeamRegion] = useState('');

  const navigate = useNavigate();
  const activeSeasonId = seasonId || getCurrentSeason();

  const matchTypeRegionOptions = useMemo(() => {
    const options = getMatchTypeRegions(matchTypeCatalog, matchCategory);
    return [...new Set([matchTypeRegion, ...options, 'GLOBAL'].filter(Boolean))];
  }, [matchTypeCatalog, matchCategory, matchTypeRegion]);

  const matchTypeOptions = useMemo(() => {
    const catalogOptions = getMatchTypeOptions(matchTypeCatalog, {
      category: matchCategory,
      region: matchTypeRegion,
    }).map(item => item.label);
    return [...new Set([matchType, ...catalogOptions, ...(!catalogOptions.length ? matchTypes : [])].filter(Boolean))];
  }, [matchTypeCatalog, matchCategory, matchTypeRegion, matchType, matchTypes]);

  const selectedMatchTypeEntry = useMemo(() => (
    findMatchTypeEntry(matchTypeCatalog, {
      label: matchType,
      category: matchCategory,
      region: matchTypeRegion,
    })
  ), [matchTypeCatalog, matchType, matchCategory, matchTypeRegion]);

  useEffect(() => {
    if (!selectedMatchTypeEntry) return;
    if (selectedMatchTypeEntry.category !== matchCategory) {
      setMatchCategory(selectedMatchTypeEntry.category);
    }
    if ((selectedMatchTypeEntry.region || 'GLOBAL') !== matchTypeRegion) {
      setMatchTypeRegion(selectedMatchTypeEntry.region || 'GLOBAL');
    }
  }, [selectedMatchTypeEntry, matchCategory, matchTypeRegion]);

  useEffect(() => {
    const shouldUseQuerySeason = Boolean(modeIdFromQuery && seasonIdFromQuery);
    if (shouldUseQuerySeason && seasonIdFromQuery !== seasonId) {
      setSeasonId(seasonIdFromQuery);
    } else if (!shouldUseQuerySeason && seasonId !== WRITE_SEASON_ID) {
      setSeasonId(WRITE_SEASON_ID);
    }
  }, [seasonIdFromQuery, seasonId, modeIdFromQuery]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      const userRef = doc(db, `users/${user.uid}`);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const data = userSnap.data();
        setUserProfile(data);
        const finalTeamId = teamIdFromQuery || data.teamId;
        if (finalTeamId) {
          setTeamId(finalTeamId);
          const teamRef = doc(db, `teams/${finalTeamId}`);
          const teamSnap = await getDoc(teamRef);
          if (teamSnap.exists()) {
            setTeamLogo(teamSnap.data().logo);
            setTeamName(teamSnap.data().name);
          }
        }
      }
    });
    return unsub;
  }, [teamIdFromQuery]);

  useEffect(() => {
    const pools = {
      HP: getMapPoolForSeason(activeSeasonId, 'HP'),
      SND: getMapPoolForSeason(activeSeasonId, 'SND'),
      CTL: getMapPoolForSeason(activeSeasonId, 'CTL')
    };
    if (mode && pools[mode]) {
      setMapOptions(pools[mode]);
    } else {
      setMapOptions([]);
    }
  }, [mode, activeSeasonId]);

  useEffect(() => {
    const fetchStatsConfig = async () => {
      if (!teamId) return;
      try {
        const configRef = doc(db, `teams/${teamId}/configurations/stats`);
        const configSnap = await getDoc(configRef);
        if (!configSnap.exists()) return;
        const config = configSnap.data();
        const seasonConfig = getSeasonConfig(config, seasonId || getCurrentSeason());
        const rawTeams = seasonConfig.vsTeams || [];
        const updatedMatchTypes = seasonConfig.matchTypes || [];
        const catalog = seasonConfig.matchTypeCatalog || [];
        const normalizedTeams = rawTeams.map(t => ({
          name: t.name.trim().toUpperCase(),
          region: t.region.trim().toUpperCase()
        }));
        const updatedRegions = [...new Set(normalizedTeams.map(t => t.region))].sort();
        setVsTeams(normalizedTeams);
        setRegions(updatedRegions);
        setMatchTypes(updatedMatchTypes);
        setMatchTypeCatalog(catalog);
      } catch (err) {
        console.error('Failed to fetch config:', err);
      }
    };
    fetchStatsConfig();
  }, [teamId, seasonId]);

  const applyEditData = useCallback((data) => {
    setMatchDate(data.Date || '');
    setMode(data.Mode || '');
    setSelectedMap(data.Map || '');
    setMatchType(data.MatchType || '');
    setMatchCategory(data.MatchCategory || 'PRACTICE');
    setMatchTypeRegion(data.MatchTypeRegion || 'GLOBAL');
    setMatchResult(data.WinLose || '');
    setPointsFor(data.PF?.toString() || '');
    setPointsAgainst(data.PA?.toString() || '');
    setRegion(data.EnemyRegion || '');
    setEnemyTeam(data.EnemyTeam || '');
    setTeamRegion(data.TeamRegion || '');
    setTeamName(data.Team || '');
  }, []);

  const formState = {
    matchDate,
    mode,
    matchType,
    matchTypeId: selectedMatchTypeEntry?.id || '',
    matchCategory: selectedMatchTypeEntry?.category || matchCategory || '',
    matchTypeRegion: selectedMatchTypeEntry?.region || matchTypeRegion || '',
    teamName,
    teamRegion,
    region,
    enemyTeam,
    pointsFor,
    pointsAgainst,
    matchResult,
    selectedMap
  };

  const resetScores = () => {
    setPointsFor('');
    setPointsAgainst('');
    setMatchResult('');
  };

  const {
    isEdit,
    isSubmitting,
    isDeleting,
    mirrorEnabled,
    recordStatus,
    staleMirrorWarning,
    staleBaseWarning,
    orphanWarning,
    linkedNoticeType,
    hideLinkedSubmissionNotice,
    showMirrorConfirmModal,
    showMirrorEditModal,
    showDeleteConfirmModal,
    handleSubmit,
    handleDelete,
    openDeleteConfirm,
    cancelDelete,
    closeMirrorEdit,
    setHideLinkedSubmissionNotice,
    confirmMirror,
    cancelMirror,
    setMirrorEnabled,
  } = useMirrorSubmission({
    db,
    teamId,
    seasonId,
    modeIdFromQuery,
    formRef,
    formState,
    applyEditData,
    onResetScores: resetScores,
    onAfterDelete: () => navigate('/my-stats'),
    onNavigateExisting: (existingId) => {
      navigate(`/submit-enemy-stats?teamId=${teamId}&seasonId=${seasonId}&modeId=${existingId}`);
    },
  });

  const handleBackToStats = () => {
    sessionStorage.setItem('returnToTab', 'statshub');
    navigate('/my-stats');
  };

  const handleOpenConfig = () => {
    navigate('/settings?tab=database-config');
  };

  return (
    <>
      <TopBar
        userProfile={userProfile}
        teamLogo={teamLogo}
        setUserProfile={setUserProfile}
        setTeamLogo={setTeamLogo}
      />
      <main className="team-summary-page enemy-summary-page">
        <div className="team-summary-shell">
          <button
            type="button"
            className="stats-back-button"
            onClick={handleBackToStats}
          >
            <FiChevronLeft size={18} />
            Back to Stats Hub
          </button>

          <EnemyStatsForm
            formRef={formRef}
            onSubmit={handleSubmit}
            onOpenConfig={handleOpenConfig}
            state={{
              matchDate,
              teamRegion,
              teamName,
              region,
              enemyTeam,
              matchType,
              matchCategory,
              matchTypeRegion,
              mode,
              selectedMap,
              matchResult,
              pointsFor,
              pointsAgainst,
              mirrorEnabled,
              recordStatus,
              staleMirrorWarning,
              staleBaseWarning,
              orphanWarning
            }}
            actions={{
              setMatchDate,
              setTeamRegion,
              setTeamName,
              setRegion,
              setEnemyTeam,
              setMatchType,
              setMatchCategory,
              setMatchTypeRegion,
              setMode,
              setSelectedMap,
              setMatchResult,
              setPointsFor,
              setPointsAgainst,
              setMirrorEnabled
            }}
            options={{ regions, vsTeams, matchTypes, matchTypeOptions, matchTypeRegionOptions, mapOptions }}
            flags={{ isSubmitting, teamId, isEdit }}
            onDeleteClick={openDeleteConfirm}
          />
        </div>
      </main>

      <MirrorSubmissionModals
        showMirrorConfirm={showMirrorConfirmModal}
        showMirrorEdit={showMirrorEditModal}
        showDeleteConfirm={showDeleteConfirmModal}
        linkedNoticeType={linkedNoticeType}
        hideLinkedSubmissionNotice={hideLinkedSubmissionNotice}
        onConfirmMirror={confirmMirror}
        onCancelMirror={cancelMirror}
        onCloseMirrorEdit={closeMirrorEdit}
        onToggleHideLinkedNotice={setHideLinkedSubmissionNotice}
        onConfirmDelete={handleDelete}
        onCancelDelete={cancelDelete}
        isDeleting={isDeleting}
      />

      <ToastContainer position="bottom-right" autoClose={3000} />
    </>
  );
};

export default AddEnemyDashboard;
